import * as formatcoords from 'formatcoords';
import { LatLng } from '@agm/core';

import { toDegrees, toRadians } from './helpers';
import { ELLIPSOID_FLATTENING, ELLIPSOID_MAJOR_AXIS, ELLIPSOID_MINOR_AXIS } from './constants';
import { VincentyInverseResult } from './vincenty-inverse-result';


/**
 * Model for geographic location identified by a latitude and longitude
 */
export class LatLon {
  private _lat: number;
  private _lon: number;

  /**
   * Constructs an instance of the LatLon class
   * @param lat {number} Latitude in degrees
   * @param lon {number} Longitude in degrees
   */
  constructor(lat: number, lon: number) {
    this.lat = lat;
    this.lon = lon;
  }

  /**
   * Parses a LatLon instance from the LatLng type in the Angular Google Maps (AGM) library
   */
  static fromAgm(value: LatLng): LatLon {
    return new LatLon(value.lat(), value.lng());
  }

  /**
   * Gets the latitude in degrees (positive is Northern hemisphere, negative is Southern hemisphere)
   */
  get lat(): number {
    return this._lat;
  }

  /**
   * Sets the latitude in degrees (positive is Northern hemisphere, negative is Southern hemisphere)
   * @param v {number}
   */
  set lat(v: number) {
    if (v < -90 || v > 90) {
      throw new Error(`Invalid value for latitude (value must be in the range [-90, 90]): ${v}`);
    }
    this._lat = v;
  }

  /**
   * Gets the longitude in degrees (positive is Eastern hemisphere, negative is Western hemisphere)
   * @param v {number}
   */
  get lon(): number {
    return this._lon;
  }

  /**
   * Sets the longitude in degrees (positive is Eastern hemisphere, negative is Western hemisphere)
   * @param v {number}
   */
  set lon(v: number) {
    if (v < -180 || v > 180) {
      throw new Error(`Invalid value for longitude (value must be in the range [-180, 180]): ${v}`);
    }
    this._lon = v;
  }

  /**
   * Compute the distance in meters between this and the given point using Vincenty's formula (inverse solution).
   * While more computationally intensive, this method is more accurate than either the Haversine or the Law of Cosines
   * formulae (especially for small distances).
   * @param {LatLon} point Latitude/longitude of the destination point
   * @return {Number} Distance in meters between points or NaN if failed to converge
   */
  distanceTo(point: LatLon) {
    try {
      return Number(this.vincentyInverse(point).distance.toFixed(3));   // round to 1mm precision
    } catch (e) {
      return NaN;   // failed to converge
    }
  }

  /**
   * Vincenty inverse calculation
   * @param {LatLon} point Latitude/longitude of destination point
   * @returns {VincentyInverseResult} Result
   */
  vincentyInverse(point: LatLon): VincentyInverseResult {
    const p1 = this, p2 = point;
    if (p1.lon === -180) { p1.lon = 180; }
    const φ1 = toRadians(p1.lat), λ1 = toRadians(p1.lon);
    const φ2 = toRadians(p2.lat), λ2 = toRadians(p2.lon);

    const a = ELLIPSOID_MAJOR_AXIS;
    const b = ELLIPSOID_MINOR_AXIS;
    const f = ELLIPSOID_FLATTENING;

    const L = λ2 - λ1;
    const tanU1 = (1 - f) * Math.tan(φ1), cosU1 = 1 / Math.sqrt((1 + tanU1 * tanU1)), sinU1 = tanU1 * cosU1;
    const tanU2 = (1 - f) * Math.tan(φ2), cosU2 = 1 / Math.sqrt((1 + tanU2 * tanU2)), sinU2 = tanU2 * cosU2;

    let sinλ, cosλ, sinSqσ, sinσ = 0, cosσ = 0, σ = 0, sinα, cosSqα = 0, cos2σM = 0, C;

    let λ = L, λʹ, iterations = 0;
    do {
      sinλ = Math.sin(λ);
      cosλ = Math.cos(λ);
      sinSqσ = (cosU2 * sinλ) * (cosU2 * sinλ) + (cosU1 * sinU2 - sinU1 * cosU2 * cosλ) * (cosU1 * sinU2 - sinU1 * cosU2 * cosλ);
      if (sinSqσ === 0) { break; } // co-incident points
      sinσ = Math.sqrt(sinSqσ);
      cosσ = sinU1 * sinU2 + cosU1 * cosU2 * cosλ;
      σ = Math.atan2(sinσ, cosσ);
      sinα = cosU1 * cosU2 * sinλ / sinσ;
      cosSqα = 1 - sinα * sinα;
      cos2σM = (cosSqα !== 0) ? (cosσ - 2 * sinU1 * sinU2 / cosSqα) : 0; // equatorial line: cosSqα=0 (§6)
      C = f / 16 * cosSqα * (4 + f * (4 - 3 * cosSqα));
      λʹ = λ;
      λ = L + (1 - C) * f * sinα * (σ + C * sinσ * (cos2σM + C * cosσ * (-1 + 2 * cos2σM * cos2σM)));
      if (Math.abs(λ) > Math.PI) { throw new Error('λ > π'); }
    } while (Math.abs(λ - λʹ) > 1e-12 && ++iterations < 1000);
    if (iterations >= 1000) { throw new Error('Formula failed to converge'); }

    const uSq = cosSqα * (a * a - b * b) / (b * b);
    const A = 1 + uSq / 16384 * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
    const B = uSq / 1024 * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
    const Δσ = B * sinσ * (cos2σM + B / 4 * (cosσ * (-1 + 2 * cos2σM * cos2σM) -
      B / 6 * cos2σM * (-3 + 4 * sinσ * sinσ) * (-3 + 4 * cos2σM * cos2σM)));

    const s = b * A * (σ - Δσ);

    let α1 = Math.atan2(cosU2 * sinλ,  cosU1 * sinU2 - sinU1 * cosU2 * cosλ);
    let α2 = Math.atan2(cosU1 * sinλ, -sinU1 * cosU2 + cosU1 * sinU2 * cosλ);

    α1 = (α1 + 2 * Math.PI) % (2 * Math.PI); // normalise to 0..360
    α2 = (α2 + 2 * Math.PI) % (2 * Math.PI); // normalise to 0..360

    return {
      distance: s,
      initialBearing: s === 0 ? NaN : toDegrees(α1),
      finalBearing: s === 0 ? NaN : toDegrees(α2),
      iterations: iterations,
    };
  }

  /**
   * Format location for display
   * @param mask {string} mask to determine how to display location
   * @param opts {object} Options:
   *    - latLonSeparator: The separator to use between the lat and lon values (defaults to space)
   *    - decimalPlaces: The number of decimal places to return (defaults to 5)
   * @returns {string} formatted location
   */
  format(mask: string, opts?: object): string {
    if (!mask || mask.length < 1) {
      mask = 'FFf';
    }
    return formatcoords(this.lat, this.lon).format(mask, opts);
  }
}
